import { useState, useMemo } from 'react';
import { FileText, Plus, Search } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useEmailTemplates, type EmailTemplate } from '@/hooks/email/useEmailTemplates';

interface EmailTemplateSelectPopoverProps {
  onSelectTemplate: (template: EmailTemplate) => void;
  onCreateNew: () => void;
}

/** Strip HTML tags to get plain text preview */
function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function EmailTemplateSelectPopover({
  onSelectTemplate,
  onCreateNew,
}: EmailTemplateSelectPopoverProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { templates, loading } = useEmailTemplates();

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
    );
  }, [templates, search]);

  /** Templates grouped by category, order within group preserved (already sorted by usage_count from server) */
  const grouped = useMemo(() => {
    const map = new Map<string, EmailTemplate[]>();
    for (const tpl of filtered) {
      const list = map.get(tpl.category) ?? [];
      list.push(tpl);
      map.set(tpl.category, list);
    }
    return map;
  }, [filtered]);

  const handleSelect = (tpl: EmailTemplate) => {
    onSelectTemplate(tpl);
    setOpen(false);
    setSearch('');
  };

  const handleCreateNew = () => {
    setOpen(false);
    setSearch('');
    onCreateNew();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Templates de email"
          className={cn(
            'flex items-center gap-1.5 px-2 py-1.5 rounded text-xs transition-colors',
            open
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
        >
          <FileText className="h-4 w-4" />
          <span className="hidden sm:inline">Templates</span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        side="bottom"
        className="w-80 p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-semibold text-foreground">Templates de email</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs text-primary hover:text-primary hover:bg-primary/10"
            onClick={handleCreateNew}
          >
            <Plus className="h-3.5 w-3.5" />
            Criar
          </Button>
        </div>

        {/* Search */}
        <div className="px-3 py-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar template..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-sm bg-muted/40 border-0 focus-visible:ring-1"
            />
          </div>
        </div>

        {/* Template list */}
        <div className="max-h-72 overflow-y-auto">
          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Carregando...</div>
          ) : grouped.size === 0 ? (
            <div className="py-6 flex flex-col items-center gap-3">
              <p className="text-sm text-muted-foreground">Nenhum template encontrado</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5"
                onClick={handleCreateNew}
              >
                <Plus className="h-3.5 w-3.5" />
                Criar novo
              </Button>
            </div>
          ) : (
            Array.from(grouped.entries()).map(([category, items]) => (
              <div key={category}>
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/30">
                  {category}
                </div>
                {items.map((tpl) => {
                  const preview = stripHtml(tpl.body_html);
                  const varCount = tpl.variables.length;
                  return (
                    <button
                      key={tpl.id}
                      type="button"
                      className="w-full text-left px-3 py-2.5 hover:bg-muted/60 transition-colors border-b border-muted/30 last:border-0"
                      onClick={() => handleSelect(tpl)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-foreground truncate">
                          {tpl.name}
                        </span>
                        {varCount > 0 && (
                          <span className="shrink-0 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            {varCount}v
                          </span>
                        )}
                      </div>
                      {preview && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {preview}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
